import { Fragment, type ReactNode, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { MobileNav } from './MobileNav';
import { SessionEndedNotice } from './SessionEndedNotice';
import { Navigate } from '@tanstack/react-router';
import { WifiOff, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useCurrentFarm, useReconcileFarmSelection } from '@/hooks/useCurrentFarm';
import { useFarms } from '@/hooks/useFarms';
import { useAuth } from '@/hooks/useAuth';
import { useMobileNavStore } from '@/stores/mobileNavStore';

interface AppLayoutProps {
  children: ReactNode;
  /** Wait for the farm list and remount children per farm. Off for the route error boundary. */
  farmGate?: boolean;
}

// Issue #130: Vite's define() normally injects these, but a misconfigured
// dev server (or typecheck-only path) can leave them undefined — which
// throws ReferenceError at runtime. Guard at read-time.
const buildId: string =
  typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev';
const buildBranch: string =
  typeof __BUILD_BRANCH__ !== 'undefined' ? __BUILD_BRANCH__ : '';

export function AppLayout({ children, farmGate = true }: AppLayoutProps) {
  const mobileNavOpen = useMobileNavStore((s) => s.open);
  const setMobileNavOpen = useMobileNavStore((s) => s.setOpen);
  // The store outlives this layout: without this, signing out with the menu open reopens it on the next sign-in.
  useEffect(() => () => setMobileNavOpen(false), [setMobileNavOpen]);
  const { farmId, isLoading: farmLoading } = useCurrentFarm();
  const { isFailed, isOffline, refetch } = useFarms();
  const { session, loading: authLoading } = useAuth();
  useReconcileFarmSelection();
  const buildIdShort = buildId === 'dev' ? 'dev' : buildId.slice(0, 7);
  const buildLabel = buildBranch
    ? `Build ${buildIdShort} (${buildBranch})`
    : `Build ${buildIdShort}`;

  // Signed out (another tab, expired refresh): nothing here can load, so leave instead of spinning.
  if (!authLoading && !session) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop sidebar - fixed */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <Sidebar />
      </div>

      {/* Mobile header */}
      <div className="lg:hidden">
        <Header onMenuClick={() => setMobileNavOpen(true)} />
      </div>

      {/* Mobile navigation drawer */}
      <MobileNav
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />

      {/* Main content */}
      <main className="lg:pl-64">
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <SessionEndedNotice />
          {/* Keyed by farm so a switch remounts the page: no observer carries the old
              farm's data (global keepPreviousData) or local state into the new farm. */}
          {!farmGate ? (
            children
          ) : isFailed || isOffline ? (
            <div className="max-w-md mx-auto mt-16 text-center p-8">
              {isOffline ? (
                <WifiOff className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              ) : (
                <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              )}
              <h1 className="text-lg font-semibold text-gray-900">
                {isOffline ? "You're offline" : "Couldn't load your farms"}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {isOffline
                  ? 'Reconnect to the internet and try again.'
                  : 'Check your connection and try again.'}
              </p>
              <Button className="mt-5" variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : farmLoading ? (
            <div className="flex justify-center py-20"><LoadingSpinner /></div>
          ) : (
            <Fragment key={farmId ?? 'no-farm'}>{children}</Fragment>
          )}
        </div>
      </main>

      <div className="pointer-events-none select-none fixed bottom-2 right-3 text-[10px] text-gray-400">
        {buildLabel}
      </div>
    </div>
  );
}
